import React, { useEffect, useRef, useState } from 'react'
import { CheckIcon, Loader2Icon, PlusIcon } from 'lucide-react'
import type { ConversationLabel } from '../api/conversationLabels'

interface Props {
  labels: ConversationLabel[]
  /** Ids of labels currently on the conversation */
  selectedIds: Set<string>
  /** Toggle one label on/off; `assigned` is the current state */
  onToggle: (label: ConversationLabel, assigned: boolean) => void
  /** Create a new label (and typically assign it right away) */
  onCreate: (name: string) => Promise<void>
  onClose: () => void
  /** Horizontal anchor relative to the positioned parent */
  align?: 'left' | 'right'
  className?: string
}

/**
 * Checkbox-style picker for conversation labels with inline creation, used
 * from the Inbox conversation header and list-row actions menu. Presentational
 * only — the parent owns the data and mutations. Kept as a separate component
 * file (not an Inbox render helper) so its text input keeps focus across
 * parent re-renders.
 */
export function LabelPickerPopover({
  labels,
  selectedIds,
  onToggle,
  onCreate,
  onClose,
  align = 'right',
  className = '',
}: Props) {
  const [newName, setNewName] = useState('')
  const [creating, setCreating] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function onDocMouseDown(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) onClose()
    }
    document.addEventListener('mousedown', onDocMouseDown)
    return () => document.removeEventListener('mousedown', onDocMouseDown)
  }, [onClose])

  const trimmed = newName.trim()
  const duplicate = labels.some((l) => l.name.toLowerCase() === trimmed.toLowerCase())

  async function handleCreate() {
    if (!trimmed || duplicate || creating) return
    setCreating(true)
    try {
      await onCreate(trimmed)
      setNewName('')
    } finally {
      setCreating(false)
    }
  }

  return (
    <div
      ref={rootRef}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
      className={`absolute ${align === 'right' ? 'right-0' : 'left-0'} top-full mt-1 z-40 w-60 bg-white rounded-xl shadow-xl border border-gray-200 py-1 ${className}`}
    >
      <div className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
        Labels
      </div>

      <div className="max-h-56 overflow-y-auto">
        {labels.length === 0 && (
          <p className="px-3 py-2 text-xs text-gray-400">No labels yet — create one below.</p>
        )}
        {labels.map((label) => {
          const assigned = selectedIds.has(label.id)
          return (
            <button
              key={label.id}
              type="button"
              onClick={() => onToggle(label, assigned)}
              className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <span
                className="w-3 h-3 rounded-full flex-shrink-0"
                style={{ backgroundColor: label.color }}
              />
              <span className="flex-1 truncate">{label.name}</span>
              {assigned && <CheckIcon className="w-4 h-4 text-blue-600 flex-shrink-0" />}
            </button>
          )
        })}
      </div>

      <div className="border-t border-gray-100 px-3 py-2">
        <div className="flex items-center gap-1.5">
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
            placeholder="New label..."
            maxLength={40}
            className="flex-1 min-w-0 px-2 py-1.5 text-xs border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <button
            type="button"
            onClick={handleCreate}
            disabled={!trimmed || duplicate || creating}
            title={duplicate ? 'A label with this name already exists' : 'Create label'}
            className="p-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {creating ? (
              <Loader2Icon className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <PlusIcon className="w-3.5 h-3.5" />
            )}
          </button>
        </div>
        {duplicate && trimmed && (
          <p className="mt-1 text-[11px] text-amber-600">That label already exists.</p>
        )}
      </div>
    </div>
  )
}
